import { MessageCircle, Gift, CreditCard, RotateCcw, Banknote, Smartphone, ShoppingCart, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { readLocalCustomer, isLocalCustomerComplete, saveLocalCustomer } from '@/lib/localCustomer';
import { normalizeCustomerPhone } from '@/lib/phone';
import { persistCheckoutChatContext } from '@/lib/checkoutChatContext';
import { mustCollectAddressForProfile } from '@/lib/assistant/assistantProfileUtils';
import { loadOrderIntoCart, getOrderToRepeat } from '@/lib/assistant/assistantRepeatOrder';
import { buildOrderPreview } from '@/lib/assistant/buildOrderPreview';
import {
  enrichOrderPreviewWithMapDelivery,
  previewWithLoadingDelivery,
} from '@/lib/assistant/enrichOrderPreviewWithMapDelivery';
import {
  formatTransferBankMessage,
  getCheckoutCustomerSnapshot,
  getLocalSuggestedUpsellProducts,
  getMissingCheckoutFields,
  mergeCheckoutPrefill,
  resolveCheckoutBootstrapStep,
} from '@/lib/assistant/assistantOrderCloseFlow';
import {
  CHECKOUT_PAYMENT_LABELS,
  getEnabledPaymentMethodsForCheckout,
  selectionToCanonicalPayment,
} from '@/lib/checkout/paymentMethods';
import { readLastOrderForBusiness } from '@/lib/localLastOrder';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';

/** Checkout embebido, perfil y repetir pedido anterior. */
export function bindAssistantChatCheckout(ctx) {
  const {
    businessId,
    routeBid,
    navigate,
    menuData,
    profileDraft,
    lastOrder,
    setProfileDraft,
    setAssistantCheckoutOpen,
    setCheckoutBootstrap,
    sessionMemoryRef,
    assistantCheckoutOpen,
    getCartCount,
    getCartSnapshot,
    addToCart,
    removeFromCart,
    updateQuantity,
    addBotMessage,
    patchMessageAt,
    patchMessageById,
    patchLastOrderSummary,
    refreshOrderSummaries,
    addUserMessage,
    getHandleMainChoice,
  } = ctx;

  const cartLines = () =>
    (typeof getCartSnapshot === 'function' ? getCartSnapshot() : cart) || [];

  const touchCheckoutFromAssistant = () => {
    persistCheckoutChatContext(businessId, {
      servicePreference: sessionMemoryRef.current.servicePreference,
      scheduledTime: sessionMemoryRef.current.scheduledTime,
      mood: sessionMemoryRef.current.mood,
    });
  };

  const buildCheckoutOrderPayload = () => {
    const prefill = sessionMemoryRef.current.pendingCheckoutPrefill || {};
    const snap = getCheckoutCustomerSnapshot(businessId, prefill);
    return {
      ...prefill,
      customerName: snap.name,
      customerPhone: snap.phone,
      address: snap.address || prefill.address || null,
      serviceType: prefill.serviceType || prefill.service_type || 'delivery',
    };
  };

  const openAssistantCheckout = (bootstrap = null) => {
    if (getCartCount() <= 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    touchCheckoutFromAssistant();
    const pending = sessionMemoryRef.current.pendingCheckoutPrefill;
    const customer = readLocalCustomer(businessId);
    const orderPayload = {
      ...(pending && typeof pending === 'object' ? pending : {}),
      ...(bootstrap?.order || {}),
      ...buildCheckoutOrderPayload(),
    };
    const serviceTypes = menuData?.business?.service_types || [];
    const merged = {
      ...(bootstrap || {}),
      mode: bootstrap?.mode || 'change',
      order: orderPayload,
      initialStep:
        bootstrap?.initialStep ||
        resolveCheckoutBootstrapStep(orderPayload, customer, serviceTypes),
    };
    if (pending && typeof pending === 'object') {
      sessionMemoryRef.current.pendingCheckoutPrefill = null;
    }
    setCheckoutBootstrap?.(merged);
    setAssistantCheckoutOpen(true);
  };

  const checkoutHintsFromPrefill = (prefill = null) => {
    const pending = prefill || sessionMemoryRef.current.pendingCheckoutPrefill || {};
    const snap = getCheckoutCustomerSnapshot(businessId, pending);
    return {
      address: pending.address || snap.address || null,
      paymentMethod: pending.paymentMethod || pending.payment_method || null,
      cashAmount: pending.cash_amount ?? pending.cashAmount ?? null,
      serviceType:
        pending.serviceType ||
        pending.service_type ||
        (pending.address || snap.address ? 'delivery' : sessionMemoryRef.current.servicePreference === 'pickup' ? 'retiro' : 'delivery'),
      tip: pending.tip ?? null,
      tipPercent: pending.tipPercent ?? pending.tip_percent ?? null,
    };
  };

  const applyCachedMapDeliveryQuote = (preview) => {
    const cached = sessionMemoryRef.current.mapDeliveryQuote;
    if (
      !cached ||
      !preview?.address ||
      (preview.delivery?.status !== 'needs_map' && preview.delivery?.status !== 'loading')
    ) {
      return preview;
    }
    const fee = typeof cached.fee === 'number' ? cached.fee : 0;
    const tip = preview.tip ?? 0;
    return {
      ...preview,
      delivery: {
        fee,
        status: 'ok',
        zoneName: null,
        hint: null,
        distanceKm: cached.distanceKm ?? null,
      },
      total: preview.subtotal + fee + tip,
      warnings: (preview.warnings || []).filter(
        (w) => !/delivery/i.test(String(w || ''))
      ),
    };
  };

  const buildCheckoutPreview = (prefill = null) => {
    const business = menuData?.business;
    const preview = buildOrderPreview(cartLines(), business, checkoutHintsFromPrefill(prefill));
    return applyCachedMapDeliveryQuote(preview);
  };

  const refreshOrderSummaryInMessages = () => {
    refreshOrderSummaries(buildCheckoutPreview());
  };

  const handleOrderSummaryLineQty = (lineId, delta) => {
    const line = cartLines().find((l) => String(l.id) === String(lineId));
    if (!line) return;
    const next = Math.max(1, Math.min(99, (Number(line.quantity) || 1) + delta));
    updateQuantity(lineId, next);
    setTimeout(() => refreshOrderSummaryInMessages(), 0);
  };

  const handleOrderSummaryRemoveLine = (lineId) => {
    removeFromCart(lineId, { silent: true });
    setTimeout(() => {
      if (getCartCount() <= 0) {
        refreshOrderSummaries(null);
        addBotMessage('Saqué todo del pedido. Decime qué querés pedir.');
        return;
      }
      refreshOrderSummaryInMessages();
    }, 0);
  };

  const mergePrefillFromPreview = (prefill, preview, extras = {}) => {
    mergeCheckoutPrefill(sessionMemoryRef, {
      ...(prefill || {}),
      ...extras,
      tip: preview.tip,
    });
  };

  const buildCartSummaryFollowUpOptions = () => [
    {
      label: 'Seguir comprando',
      value: 'cart-continue-shopping',
      icon: ShoppingCart,
      action: () => {
        addUserMessage('Seguir comprando');
        const handleMain = getHandleMainChoice();
        if (typeof handleMain === 'function') {
          handleMain('order');
          return;
        }
        addBotMessage('Dale, decime qué más querés — podés decir cantidades, sin ingredientes o bebidas.');
      },
    },
    {
      label: 'Terminar y pagar',
      value: 'cart-finish-pay',
      icon: CreditCard,
      variant: 'emphasis',
      action: () => {
        addUserMessage('Terminar y pagar');
        showCheckoutPreferencePrompt();
      },
    },
    {
      label: 'Agregar o cambiar datos',
      value: 'cart-edit-data',
      icon: Plus,
      action: () => {
        addUserMessage('Agregar o cambiar datos');
        touchCheckoutFromAssistant();
        openAssistantCheckout({ mode: 'change' });
      },
    },
  ];

  const showCartSummaryInChat = () => {
    if (getCartCount() <= 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    addUserMessage('Ver lo que llevás');
    const business = menuData?.business;
    let preview = buildCheckoutPreview();
    const needsMapQuote =
      Boolean(businessId && preview.address && preview.delivery?.status === 'needs_map');
    if (needsMapQuote) preview = previewWithLoadingDelivery(preview);

    const summaryMsg = addBotMessage('Así va tu pedido 👇', buildCartSummaryFollowUpOptions(), null, {
      orderSummary: preview,
      orderSummaryEditable: true,
    });

    if (needsMapQuote) {
      void (async () => {
        try {
          const hints = checkoutHintsFromPrefill(null);
          const { preview: enriched, markerPosition, formattedAddress } =
            await enrichOrderPreviewWithMapDelivery(
              buildCheckoutPreview(),
              businessId,
              business,
              hints
            );
          if (enriched.delivery?.status === 'ok' && typeof enriched.delivery.fee === 'number') {
            sessionMemoryRef.current.mapDeliveryQuote = {
              fee: enriched.delivery.fee,
              distanceKm: enriched.delivery.distanceKm ?? null,
            };
          }
          patchLastOrderSummary(enriched);
          if (summaryMsg?.id) patchMessageById(summaryMsg.id, { orderSummary: enriched });
          mergePrefillFromPreview(null, enriched, {
            markerPosition: markerPosition || undefined,
            address: formattedAddress || enriched.address,
          });
        } catch (err) {
          console.error('assistant cart summary delivery quote failed', err);
          const fallback = buildCheckoutPreview();
          patchLastOrderSummary(fallback);
          if (summaryMsg?.id) patchMessageById(summaryMsg.id, { orderSummary: fallback });
        }
      })();
    }
  };

  const showFinalOrderSummary = (prefill = null) => {
    const business = menuData?.business;
    let preview = buildCheckoutPreview(prefill);
    const needsMapQuote =
      Boolean(businessId && preview.address && preview.delivery?.status === 'needs_map');
    if (needsMapQuote) preview = previewWithLoadingDelivery(preview);

    mergePrefillFromPreview(prefill, preview);
    const summaryMsg = addBotMessage('Este es el resumen de tu pedido:', null, null, {
      orderSummary: preview,
      orderSummaryEditable: true,
    });

    if (needsMapQuote) {
      void (async () => {
        try {
          const hints = checkoutHintsFromPrefill(prefill);
          const { preview: enriched, markerPosition, formattedAddress } =
            await enrichOrderPreviewWithMapDelivery(
              buildCheckoutPreview(prefill),
              businessId,
              business,
              hints
            );
          if (enriched.delivery?.status === 'ok' && typeof enriched.delivery.fee === 'number') {
            sessionMemoryRef.current.mapDeliveryQuote = {
              fee: enriched.delivery.fee,
              distanceKm: enriched.delivery.distanceKm ?? null,
            };
          }
          patchLastOrderSummary(enriched);
          if (summaryMsg?.id) patchMessageById(summaryMsg.id, { orderSummary: enriched });
          mergePrefillFromPreview(prefill, enriched, {
            markerPosition: markerPosition || undefined,
            address: formattedAddress || prefill?.address || enriched.address,
          });
        } catch (err) {
          console.error('assistant delivery quote failed', err);
          const fallback = buildCheckoutPreview(prefill);
          patchLastOrderSummary(fallback);
          if (summaryMsg?.id) patchMessageById(summaryMsg.id, { orderSummary: fallback });
        }
      })();
    }

    setTimeout(() => {
      addBotMessage('¿Está bien así o necesitás cambiar algo?', [
        {
          label: 'Confirmar y enviar',
          value: 'checkout-confirm',
          icon: MessageCircle,
          variant: 'emphasis',
          action: () => {
            addUserMessage('Confirmar y enviar');
            touchCheckoutFromAssistant();
            openAssistantCheckout({ mode: 'change', initialStep: 'review' });
          },
        },
        {
          label: 'Agregar algo más',
          value: 'checkout-more',
          icon: MessageCircle,
          action: () =>
            addBotMessage('Dale, decime qué más querés — podés decir cantidades, sin ingredientes o bebidas.'),
        },
        {
          label: 'Cambiar datos o pago',
          value: 'checkout-edit',
          icon: CreditCard,
          action: () => {
            addUserMessage('Quiero cambiar datos o pago');
            touchCheckoutFromAssistant();
            openAssistantCheckout({ mode: 'change' });
          },
        },
      ]);
    }, 280);
  };

  const PAYMENT_CHAT_OPTIONS = {
    efectivo: { value: 'pay-cash', icon: Banknote },
    transferencia: { value: 'pay-transfer', icon: Smartphone },
    online_manual_card: { value: 'pay-card', icon: CreditCard },
    online_manual_mercadopago: { value: 'pay-mp', icon: Smartphone },
  };

  const promptPaymentMethod = () => {
    const { methodIds } = getEnabledPaymentMethodsForCheckout(menuData?.business);
    const options = methodIds
      .map((selectionId) => {
        const meta = PAYMENT_CHAT_OPTIONS[selectionId];
        if (!meta) return null;
        return {
          label: CHECKOUT_PAYMENT_LABELS[selectionId],
          value: meta.value,
          icon: meta.icon,
          action: () => selectPaymentMethod(selectionId),
        };
      })
      .filter(Boolean);

    addBotMessage('¿Cómo vas a pagar?', options);
  };

  const selectPaymentMethod = (selectionId) => {
    const { canonical, onlineManualKind } = selectionToCanonicalPayment(selectionId);
    const label = CHECKOUT_PAYMENT_LABELS[selectionId] || canonical;
    addUserMessage(label);
    mergeCheckoutPrefill(sessionMemoryRef, {
      paymentMethod: canonical,
      ...(onlineManualKind ? { onlineManualKind } : {}),
    });
    sessionMemoryRef.current.awaitingOrderField = null;

    if (canonical === 'efectivo') {
      sessionMemoryRef.current.awaitingOrderField = 'cash_amount';
      addBotMessage('¿Con cuánto pagás? (opcional, para calcular el vuelto — ej: 20000 o 25 lucas)', [
        {
          label: 'Seguir sin monto',
          value: 'cash-skip',
          icon: MessageCircle,
          action: () => {
            addUserMessage('Seguir sin monto');
            sessionMemoryRef.current.awaitingOrderField = null;
            continueOrderCloseFlow();
          },
        },
      ]);
      return;
    }

    if (canonical === 'transferencia') {
      const preview = buildCheckoutPreview();
      addBotMessage(formatTransferBankMessage(menuData?.business, preview.total));
    }

    continueOrderCloseFlow();
  };

  const continueOrderCloseFlow = (prefill = null) => {
    if (prefill) mergeCheckoutPrefill(sessionMemoryRef, prefill);
    const pending = sessionMemoryRef.current.pendingCheckoutPrefill || {};
    const missing = getMissingCheckoutFields({
      business: menuData?.business,
      businessId,
      prefill: pending,
    });

    if (missing.nameOrPhone) {
      sessionMemoryRef.current.orderCloseActive = true;
      const snap = getCheckoutCustomerSnapshot(businessId, pending);
      setProfileDraft({
        name: snap.name,
        phone: snap.phone,
        address: snap.address || pending.address || '',
      });
      addBotMessage('Para cerrar el pedido necesito tu nombre y teléfono. Completalos abajo 👇');
      return;
    }

    if (missing.address) {
      sessionMemoryRef.current.orderCloseActive = true;
      sessionMemoryRef.current.awaitingOrderField = 'address';
      mergeCheckoutPrefill(sessionMemoryRef, { serviceType: 'delivery' });
      addBotMessage(
        '¿A qué dirección y comuna te lo mandamos? (ej: Fontt 136, Colina)\n\nPodés escribirla en el chat.'
      );
      return;
    }

    if (missing.payment) {
      sessionMemoryRef.current.orderCloseActive = true;
      promptPaymentMethod();
      return;
    }

    sessionMemoryRef.current.orderCloseActive = false;
    sessionMemoryRef.current.awaitingOrderField = null;
    showFinalOrderSummary(pending);
  };

  const promptUpsellBeforeClose = () => {
    const suggestions = getLocalSuggestedUpsellProducts(menuData, cartLines(), 3);
    if (!suggestions.length || sessionMemoryRef.current.upsellShownThisClose) {
      continueOrderCloseFlow();
      return;
    }
    sessionMemoryRef.current.upsellShownThisClose = true;
    addBotMessage(
      '¿Te tinca sumar algo más? Estas son las sugerencias del local 👇',
      [
        {
          label: 'Listo, seguir con el pedido',
          value: 'upsell-continue',
          icon: MessageCircle,
          variant: 'emphasis',
          action: () => {
            addUserMessage('Listo, seguir con el pedido');
            continueOrderCloseFlow();
          },
        },
      ],
      null,
      { suggestedProducts: suggestions }
    );
  };

  const showOrderDetailAndFollowUp = (prefill = null) => {
    try {
      if (prefill) mergeCheckoutPrefill(sessionMemoryRef, prefill);
      sessionMemoryRef.current.orderCloseActive = true;
      promptUpsellBeforeClose();
    } catch (err) {
      console.error('assistant order summary failed', err);
      try {
        continueOrderCloseFlow();
      } catch (innerErr) {
        console.error('assistant order close fallback failed', innerErr);
        addBotMessage(
          'Listo, agregué tu pedido al carrito. Tocá «Terminar pedido» arriba para ver el total y confirmar.'
        );
      }
    }
  };

  const handleOrderCloseFreeText = (text) => {
    if (!sessionMemoryRef.current.orderCloseActive) return false;
    const field = sessionMemoryRef.current.awaitingOrderField;
    const q = String(text || '').trim();
    if (!field || !q) return false;

    if (field === 'address') {
      mergeCheckoutPrefill(sessionMemoryRef, { address: q, serviceType: 'delivery' });
      sessionMemoryRef.current.awaitingOrderField = null;
      continueOrderCloseFlow();
      return true;
    }

    if (field === 'cash_amount') {
      const digits = q.replace(/\./g, '').match(/(\d{3,})/);
      const lucas = q.match(/\b(\d{1,3})\s*lucas?\b/i);
      let amount = null;
      if (lucas) amount = Number(lucas[1]) * 1000;
      else if (digits) amount = Number(digits[1]);
      if (amount != null && amount > 0) {
        mergeCheckoutPrefill(sessionMemoryRef, { cash_amount: amount });
      }
      sessionMemoryRef.current.awaitingOrderField = null;
      continueOrderCloseFlow();
      return true;
    }

    return false;
  };

  const submitProfileDraft = () => {
    if (!profileDraft) return;
    const b = menuData?.business;
    const name = String(profileDraft.name || '').trim();
    const phone = normalizeCustomerPhone(profileDraft.phone || '');
    const address = String(profileDraft.address || '').trim();
    if (!name || !phone) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    if (mustCollectAddressForProfile(b) && !address && sessionMemoryRef.current.orderCloseActive) {
      /* dirección se pide en el flujo de cierre si falta */
    } else if (mustCollectAddressForProfile(b) && !address) {
      toast.error('Necesitamos la dirección para envío a domicilio');
      return;
    }
    const prev = readLocalCustomer(businessId) || {};
    saveLocalCustomer(
      {
        name,
        phone,
        email: prev.email || '',
        address: address || prev.address || '',
      },
      businessId
    );
    mergeCheckoutPrefill(sessionMemoryRef, {
      customerName: name,
      customerPhone: phone,
      address: address || sessionMemoryRef.current.pendingCheckoutPrefill?.address || prev.address,
    });
    setProfileDraft(null);

    if (sessionMemoryRef.current.orderCloseActive) {
      addBotMessage(`Gracias, ${name.split(' ')[0] || name}. Sigo con tu pedido.`);
      setTimeout(() => continueOrderCloseFlow(), 200);
      return;
    }

    addBotMessage(
      'Listo. Guarda tus datos y únete al club de beneficios para acumular compras y ganar premios.'
    );
    setTimeout(() => {
      addBotMessage('¿Quieres agregar algo más?', [
        {
          label: 'Únete al club de beneficios',
          value: 'profile-club',
          icon: Gift,
          action: () => navigate(getPublicMenuPath(routeBid, 'profile')),
        },
        {
          label: 'Completa tus datos para terminar el pedido',
          value: 'cart',
          icon: MessageCircle,
          action: () => {
            touchCheckoutFromAssistant();
            if (getCartCount() > 0) showCheckoutPreferencePrompt();
            else getHandleMainChoice()('order');
          },
        },
      ]);
    }, 280);
  };

  const showCheckoutPreferencePrompt = () => {
    if (getCartCount() <= 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    if (assistantCheckoutOpen) return;
    if (sessionMemoryRef.current.orderCloseActive && sessionMemoryRef.current.upsellShownThisClose) {
      continueOrderCloseFlow();
      return;
    }
    sessionMemoryRef.current.upsellShownThisClose = false;
    sessionMemoryRef.current.orderCloseActive = false;
    showOrderDetailAndFollowUp();
  };

  const confirmRepeatSameData = (order) => {
    if (!order?.items?.length) return;
    addUserMessage('Mantener la información anterior');
    loadOrderIntoCart(order, addToCart, businessId);
    addBotMessage(
      'Perfecto. Mantengo tus datos anteriores.\n\nEste es el detalle — confirmá y mandá por WhatsApp en un solo paso.'
    );
    touchCheckoutFromAssistant();
    openAssistantCheckout({ mode: 'same', order, initialStep: 'review' });
  };

  const confirmRepeatWithChanges = (order) => {
    if (!order?.items?.length) return;
    addUserMessage('Cambiar dirección o forma de pago');
    loadOrderIntoCart(order, addToCart, businessId);
    addBotMessage('Dale. Ajustá tus datos en orden: nombre, dirección y pago.');
    touchCheckoutFromAssistant();
    openAssistantCheckout({ mode: 'change', order });
  };

  return {
    touchCheckoutFromAssistant,
    openAssistantCheckout,
    submitProfileDraft,
    confirmRepeatSameData,
    confirmRepeatWithChanges,
    showCheckoutPreferencePrompt,
    showOrderDetailAndFollowUp,
    showCartSummaryInChat,
    buildCheckoutPreview,
    handleOrderSummaryLineQty,
    handleOrderSummaryRemoveLine,
    handleOrderCloseFreeText,
    continueOrderCloseFlow,
  };
}
